import React, { useEffect } from 'react';
import { Input, Field, Dropdown, Option } from '@fluentui/react-components';
import { RESPONDER_ROLES_CONST, RESPONDER_STATUS_CONST, USR_TEAMS, RESPONDER_STATUS_FILTER } from "#root/utils/responders";
import { getEnumLabelByValue } from "#root/utils/enums";
import './styles.scss';
import InfiniteDropdown from '../InfiniteDropdown';
import { useInfiniteDropdown } from '#root/hooks/useInfiniteDropdown';
import { Incident, useIncidentById, useInfiniteIncidents } from '#root/queries/incidents';
import { useVAdminSelectedIncident } from '#root/queries/local';

export interface FilterState {
  incidentId?: string;
  name?: string;
  departmentOrAgency?: string;
  usarTeamNumber?: number;
  role?: number;
  status?: number;
}

export const initialFilters: FilterState = {
  incidentId: '',
  name: '',
  departmentOrAgency: '',
  usarTeamNumber: undefined,
  role: undefined,
  status: undefined
};

export interface ResponderFiltersProps {
  filters: FilterState;
  onFilterChange: (field: keyof FilterState, value: any) => void;
  className?: string;
  departmentFilterEnabled?: boolean;
  statusFilterEnabled?: boolean;
}

const ResponderFilters: React.FC<ResponderFiltersProps> = ({
  filters,
  onFilterChange,
  className = '',
  departmentFilterEnabled = true,
  statusFilterEnabled = false
}) => {
  const { selectedIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();

  useEffect(() => {
    if (selectedIncidentId && !filters.incidentId) {
      onFilterChange('incidentId', selectedIncidentId);
    }
  }, []);

  const handleIncidentChange = (id: string | null) => {
    onFilterChange('incidentId', id || '');

    if (id) {
      setVAdminSelectedIncidentId(id);
    }
  };

  const {
    selectedData: selectedIncidentData,
    setFilter: setIncidentFilter,
    queryResult: incidentQueryResult
  } = useInfiniteDropdown<Incident>({
    initialId: filters.incidentId || '',
    onChange: handleIncidentChange,
    useInfiniteQuery: useInfiniteIncidents,
    useItemById: useIncidentById
  });

  return (
    <div className={`responder-filters ${className}`}>
      <Field label="Incident" className='responder-filters__filter'>
        <InfiniteDropdown
          value={filters.incidentId}
          onChange={(_, data) => handleIncidentChange(data.value as string)}
          onFilterChange={setIncidentFilter}
          queryResult={incidentQueryResult}
          itemToOption={(incident) => ({
            value: incident.id ?? '',
            text: incident.name ?? ''
          })}
          placeholder='All Incidents'
          selectedItem={selectedIncidentData}
          clearable
        />
      </Field>
      <Field label="Name" className='responder-filters__filter'>
        <Input
          value={filters.name || ''}
          onChange={(_, data) => onFilterChange('name', data.value)}
        />
      </Field>
      {departmentFilterEnabled && (
        <Field label="Department/Agency" className='responder-filters__filter'>
          <Input
            value={filters.departmentOrAgency || ''}
            onChange={(_, data) => onFilterChange('departmentOrAgency', data.value)}
          />
        </Field>
      )}
      <Field label="US&R Team" className='responder-filters__filter usar'>
        <Dropdown
          clearable
          value={filters.usarTeamNumber !== undefined ? getEnumLabelByValue(USR_TEAMS, filters.usarTeamNumber) : ''}
          onOptionSelect={(_, data) => onFilterChange('usarTeamNumber', data.optionValue ? Number(data.optionValue) : undefined)}
        >
          {USR_TEAMS.map(team => (
            <Option key={team.value} value={String(team.value)}>
              {team.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <Field label="Role" className='responder-filters__filter role'>
        <Dropdown
          clearable
          value={filters.role !== undefined ? getEnumLabelByValue(RESPONDER_ROLES_CONST, filters.role) : ''}
          onOptionSelect={(_, data) => onFilterChange('role', data.optionValue ? Number(data.optionValue) : undefined)}
        >
          {RESPONDER_ROLES_CONST.map(role => (
            <Option key={role.value} value={String(role.value)}>
              {role.label}
            </Option>
          ))}
        </Dropdown>
      </Field>
      {statusFilterEnabled && (
        <Field label="Status" className='responder-filters__filter status'>
          <Dropdown
            clearable
            value={filters.status !== undefined ? getEnumLabelByValue(RESPONDER_STATUS_CONST, filters.status) : ''}
            onOptionSelect={(_, data) => onFilterChange('status', data.optionValue ? Number(data.optionValue) : undefined)}
          >
            {RESPONDER_STATUS_FILTER
              .map(status => (
                <Option key={status.value} value={String(status.value)}>
                  {status.label}
                </Option>
              ))}
          </Dropdown>
        </Field>
      )}
    </div>
  );
};

export default ResponderFilters;
